// PlayFirst Parent — "Where {child} is right now"
// /parent-app/where-they-are
//
// Neurodiversity-affirming, ECI-aware view. Three sections:
//   1) Headline + intro for the child's current age band
//   2) "Often around now" + "Often emerging" (positive framing)
//   3) "Things to notice" — opt-in checkboxes the parent can tick.
//      When 3+ are ticked, a soft prompt nudges them towards the
//      Collaborators page (ECI providers / speech therapists).
//
// Data is fetched from GET /api/parent-app/children/{id}/stage and ticked
// observations are PUT back so they persist between visits.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, ArrowLeft, Sparkles, Eye, Users2, Loader2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ParentAppWhereTheyAre() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticked, setTicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);

  const auth = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const fetchStage = useCallback(async () => {
    setLoading(true);
    try {
      // Honor the parent's currently-selected child from the home page.
      const list = await axios.get(`${API}/parent-app/children`, auth());
      if (!list.data || list.data.length === 0) {
        navigate('/parent-app/onboarding');
        return;
      }
      const selectedId = (typeof window !== 'undefined' && localStorage.getItem('parentapp_selected_child_id')) || null;
      const childId = (selectedId && list.data.find((c) => c.id === selectedId))
        ? selectedId
        : list.data[0].id;
      const r = await axios.get(`${API}/parent-app/children/${childId}/stage`, auth());
      setData(r.data);
      setTicked(r.data.saved_watch_observations || []);
    } catch (e) {
      toast.error("Couldn't load this page right now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }, [auth, navigate]);

  useEffect(() => { fetchStage(); }, [fetchStage]);

  const toggle = (obs) => {
    setTicked((cur) =>
      cur.includes(obs) ? cur.filter((x) => x !== obs) : [...cur, obs]
    );
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/parent-app/children/${data.child_id}/watch-observations`,
        { observations: ticked },
        auth(),
      );
      setData((d) => ({ ...d, watch_threshold_reached: !!r.data.watch_threshold_reached }));
      toast.success('Saved');
    } catch {
      toast.error('Could not save — try again');
    } finally {
      setSaving(false);
    }
  };

  const thresholdReached = useMemo(() => ticked.length >= 3, [ticked]);

  if (loading) {
    return (
      <div className={`${PFP.page} flex items-center justify-center`}>
        <Loader2 className="w-5 h-5 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/parent-app/home')}
          className="flex items-center gap-2 text-[#7C6F9C] hover:underline"
          data-testid="stage-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
        <Link to="/parent-app/home" className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-lg text-[#3a2f4f]">PlayFirst</span>
          <span className="text-[10px] uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">
            Parent
          </span>
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 pb-16 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#7C6F9C] font-semibold mb-2">
            Where {data?.child_name?.split(' ')[0] || 'your little one'} is right now
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#3a2f4f] mb-1">
            {data?.band?.headline || 'A gentle snapshot for this age'}
          </h1>
          {data?.band?.label && (
            <p className="text-sm text-stone-500 mb-3">{data.band.label}</p>
          )}
          {data?.band?.intro && (
            <p className="text-base text-stone-600 leading-relaxed">{data.band.intro}</p>
          )}
        </motion.div>

        {!data?.band && (
          <div className={`${PFP.card} p-6 text-center`} data-testid="stage-no-age">
            <p className="text-stone-600">
              We need your child's age to show this. You can add it on the{' '}
              <Link to="/parent-app/settings" className="text-[#7C6F9C] underline">Settings</Link> page.
            </p>
          </div>
        )}

        {/* Friendly current age + DOB nudge — auto-updates as the child grows */}
        {data?.age_friendly && (
          <div
            className="flex items-center justify-between gap-3 rounded-2xl bg-white/60 border border-stone-200 px-4 py-3"
            data-testid="stage-age-readout"
          >
            <div className="text-sm text-stone-600">
              Showing content for <span className="font-semibold text-[#3a2f4f]">{data.age_friendly}</span>
            </div>
            {data.needs_dob && (
              <Link
                to="/parent-app/settings"
                className="text-xs text-[#7C6F9C] underline whitespace-nowrap"
                data-testid="stage-add-dob-link"
              >
                Add birthday to auto-update
              </Link>
            )}
          </div>
        )}

        {/* Neurodiversity-affirming disclaimer — only when a neurodivergent
            identifier is present on the child's profile. */}
        {data?.neurodivergent_disclaimer && (
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl bg-gradient-to-br from-violet-50 to-rose-50/60 border border-violet-200/70 p-5"
            data-testid="stage-neurodivergent-disclaimer"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-2xl bg-white/80 text-[#7C6F9C] flex items-center justify-center flex-shrink-0">
                <Heart className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#3a2f4f] mb-1.5">
                  {data.neurodivergent_disclaimer.title}
                </p>
                <p className="text-xs sm:text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">
                  {data.neurodivergent_disclaimer.body}
                </p>
              </div>
            </div>
          </motion.section>
        )}

        {data?.band && (
          <>
            {/* Often around now */}
            <section className={`${PFP.card} p-5`} data-testid="stage-often-now">
              <div className="flex items-center gap-2 mb-3 text-[#7C6F9C]">
                <Sparkles className="w-4 h-4" />
                <p className="text-xs uppercase tracking-wider font-semibold">Often around now</p>
              </div>
              <p className="text-xs text-stone-500 mb-3">
                Many children {data.child_name?.split(' ')[0] ? `like ${data.child_name.split(' ')[0]}` : 'this age'} are doing these things — some sooner, some later. Both are okay.
              </p>
              <ul className="space-y-2">
                {(data.band.often_now || []).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-stone-700 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7C6F9C] mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Often emerging */}
            {(data.band.often_emerging || []).length > 0 && (
              <section className={`${PFP.card} p-5`} data-testid="stage-often-emerging">
                <div className="flex items-center gap-2 mb-3 text-amber-700">
                  <Sparkles className="w-4 h-4" />
                  <p className="text-xs uppercase tracking-wider font-semibold">Often emerging next</p>
                </div>
                <ul className="space-y-2">
                  {data.band.often_emerging.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-stone-700 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Anything to note — collapsible, opt-in */}
            <section
              className="rounded-2xl border border-stone-200 bg-white/70 backdrop-blur"
              data-testid="stage-watch-section"
            >
              <button
                type="button"
                onClick={() => setWatchOpen((v) => !v)}
                className="w-full px-5 py-4 flex items-center justify-between text-left"
                data-testid="stage-watch-toggle"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-stone-100 text-stone-600 flex items-center justify-center">
                    <Eye className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#3a2f4f]">Anything you'd like to note?</p>
                    <p className="text-xs text-stone-500">Optional. Things to gently keep an eye on — never a diagnosis.</p>
                  </div>
                </div>
                {watchOpen ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
              </button>

              <AnimatePresence initial={false}>
                {watchOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 pt-1 border-t border-stone-100">
                      <p className="text-xs text-stone-500 mb-3 leading-relaxed">
                        These are observations to gently notice — many children show some of these
                        without anything being "wrong". Tick the ones that feel true today.
                      </p>
                      <ul className="space-y-2">
                        {(data.band.watch_observations || []).map((obs) => {
                          const isOn = ticked.includes(obs);
                          return (
                            <li key={obs}>
                              <label
                                className={`flex items-start gap-3 p-3 rounded-2xl cursor-pointer transition-colors ${
                                  isOn ? 'bg-rose-50 border border-rose-200' : 'hover:bg-stone-50 border border-transparent'
                                }`}
                                data-testid={`stage-watch-${obs.slice(0, 20).replace(/\s/g, '-')}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  onChange={() => toggle(obs)}
                                  className="mt-1 accent-rose-600"
                                />
                                <span className="text-sm text-stone-700 leading-relaxed">{obs}</span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>

                      <div className="flex items-center justify-end mt-4">
                        <button
                          onClick={save}
                          disabled={saving}
                          className={PFP.btnPrimary + ' inline-flex items-center gap-2'}
                          data-testid="stage-watch-save"
                        >
                          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                          Save
                        </button>
                      </div>

                      {(thresholdReached || data.watch_threshold_reached) && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                          className="mt-5 rounded-2xl border border-rose-200 bg-rose-50/70 p-4"
                          data-testid="stage-threshold-prompt"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-2xl bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                              <Users2 className="w-4 h-4" />
                            </div>
                            <div className="text-sm text-rose-900 leading-relaxed">
                              <p className="font-semibold mb-1">
                                You've noticed a few things — and that matters.
                              </p>
                              <p className="text-rose-800/90 mb-3">
                                Nothing here is a diagnosis. But early-intervention support is gentle,
                                play-based, and usually short — and it's at its most powerful when it
                                starts early. Have a look at the speech-language professionals and
                                services we collaborate with.
                              </p>
                              <Link to="/parent-app/collaborators">
                                <button
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-medium hover:bg-rose-700"
                                  data-testid="stage-threshold-cta"
                                >
                                  See our collaborators
                                </button>
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </section>

            {/* Closing reassurance */}
            {data.band.closing && (
              <div className="rounded-2xl bg-gradient-to-br from-[#7C6F9C]/10 to-rose-50/40 border border-[#7C6F9C]/20 p-5">
                <p className="text-sm text-[#3a2f4f] leading-relaxed">
                  <span className="font-medium">A note from us — </span>{data.band.closing}
                </p>
              </div>
            )}
          </>
        )}

        <footer className="text-center text-xs text-stone-400 pt-4">
          PlayFirst Parent is not a diagnostic, screening or emergency tool.
        </footer>
      </main>
    </div>
  );
}
